import { Box, Button, Heading, Stack, Text } from '@chakra-ui/react'
import { faMegaphone } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import React from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'

import CohostReviewModal from './CohostReviewModal/CohostReviewModal'

const CohostReviewBanner: React.FC<{
  sessionOrSeries: Query
  pendingCohosts: Query[]
  isSession: boolean
}> = ({ sessionOrSeries, pendingCohosts, isSession }) => {
  const currentUser = useUser()
  const [showModal, setShowModal] = React.useState(false)

  const tutorId =
    (isSession ? sessionOrSeries(SessionQ.user_id) : sessionOrSeries(SeriesQ.user_id)) ?? 0
  const numCohostRequests =
    (isSession
      ? sessionOrSeries(SessionQ.countAllHosts({ approved: false }))
      : sessionOrSeries(SeriesQ.countAllHosts({ approved: false }))) ?? 0
  const allowCohosts =
    (isSession
      ? sessionOrSeries(SessionQ.allow_cohosts)
      : sessionOrSeries(SeriesQ.allow_cohosts)) ?? false
  const sessionStarted = isSession
    ? DateTime.fromISO(`${sessionOrSeries(SessionQ.start_date)}`) < DateTime.now()
    : DateTime.fromISO(`${sessionOrSeries(SeriesQ.closed_at)}`) < DateTime.now()
  const isSeriesSession = isSession ? sessionOrSeries(SessionQ.series_id) != null : false

  const displayNames = pendingCohosts.map(host => host(UserQ.display_name))
  const numRequests = displayNames.length

  return (
    <>
      {tutorId === currentUser.id && allowCohosts && !sessionStarted && !isSeriesSession ? (
        <Box bg="yellow.subtle" borderRadius={8} p={4}>
          <Stack direction="row" justify="space-between" alignItems="center">
            <FontAwesomeIcon icon={faMegaphone} color={chakraTokenToVar('fg.warning')} />
            <Box px={4}>
              <Heading size="lg" as="h3">
                {numRequests > 0
                  ? `Review Co-hosts (${numRequests})`
                  : `You're looking for co-hosts!`}
              </Heading>
              {numRequests > 0 ? (
                <Text>
                  {numRequests === 1
                    ? `You received a request from ${displayNames[0]} to become a co-host. Review the request to accept or reject.`
                    : `You received requests from ${displayNames[0]} and ${pluralize(
                        numRequests - 1,
                        'other'
                      )} to become co-hosts. Review the requests to accept or reject.`}
                </Text>
              ) : (
                <Text>
                  You currently have no requests for co-hosting. When you receive a request,
                  we&apos;ll show it here.
                </Text>
              )}
            </Box>
            <Button flexShrink={0} variant="outline" onClick={() => setShowModal(true)} my="5px">
              Review Requests
            </Button>
          </Stack>
        </Box>
      ) : null}
      <CohostReviewModal
        cohosts={pendingCohosts}
        numCohostRequests={numCohostRequests}
        showModal={showModal}
        setShowModal={setShowModal}
        isSession={isSession}
      />
    </>
  )
}

export default CohostReviewBanner
